import app from 'flarum/common/app';

/**
 * Elemental roster — the frontend half of Support\ElementRegistry.
 *
 * Elements are no longer a fixed five: admins can rename them, restyle them,
 * re-wire which ones beat which, and add their own. The roster lives in the
 * forumaker-arena.elements setting; the forum receives it already resolved
 * (see Settings\SerializeElements), while the admin panel edits the raw
 * setting and re-resolves locally through resolveElements() below.
 *
 * `name` is nullable on purpose: a builtin the admin never renamed keeps
 * resolving through the lib.elements.* locale keys, so it stays translated
 * per user language. A custom name is a plain locale-independent string.
 */

export const DEFAULT_ELEMENT_COLOR = '#888888';
export const DEFAULT_ELEMENT_ICON = 'fas fa-question';

/**
 * The original five, with the original advantage cycle (fire → wind →
 * lightning → earth → water → fire). Mirrors ElementRegistry::BUILTIN.
 * Editable in the admin panel but never removable: existing cards and
 * archived battle posts reference these keys.
 */
export const BUILTIN_ELEMENTS = [{
  key: 'fire',
  icon: 'fas fa-fire',
  color: '#A4243B',
  beats: ['wind']
}, {
  key: 'wind',
  icon: 'fas fa-wind',
  color: '#57789E',
  beats: ['lightning']
}, {
  key: 'lightning',
  icon: 'fas fa-bolt',
  color: '#7261A8',
  beats: ['earth']
}, {
  key: 'earth',
  icon: 'fas fa-mountain',
  color: '#8B635C',
  beats: ['water']
}, {
  key: 'water',
  icon: 'fas fa-water',
  color: '#45698D',
  beats: ['fire']
}];
export function isBuiltinElement(key) {
  return BUILTIN_ELEMENTS.some(e => e.key === key);
}

/**
 * The live roster. Mutated in place (length = 0 + push) rather than
 * reassigned, so every module that imported this binding — and the
 * ELEMENT_* proxies below — keeps seeing the current data through the same
 * reference. Same pattern as ARENA_CARD_BACKS in ./index.
 */
export const ARENA_ELEMENTS = [];
export function setArenaElements(elements) {
  ARENA_ELEMENTS.length = 0;
  ARENA_ELEMENTS.push(...elements);
}
export function findElement(key) {
  return ARENA_ELEMENTS.find(e => e.key === key);
}
export function elementKeys() {
  return ARENA_ELEMENTS.map(e => e.key);
}

/**
 * Display name: the admin-set one if there is one, otherwise the
 * lib.elements.* translation, otherwise the bare key (which is what a custom
 * element with an empty name falls back to).
 */
export function elementLabel(key) {
  const custom = findElement(key)?.name;
  if (custom) return custom;
  const full = `forumaker-arena.lib.elements.${key}`;
  // trans() hands back Mithril children, not a string — even with no
  // parameters it can be an array of parts. Checking `typeof === 'string'`
  // therefore fails for a translation that exists, and everything falls
  // through to the bare key ("fire" instead of "Огонь"). Join first, then
  // compare against the key id to detect a genuinely missing translation.
  const translated = app.translator.trans(full);
  const text = (Array.isArray(translated) ? translated.join('') : String(translated ?? '')).trim();
  return text && text !== full ? text : key;
}
export function elementIcon(key) {
  return findElement(key)?.icon || DEFAULT_ELEMENT_ICON;
}
export function elementColor(key) {
  return findElement(key)?.color || DEFAULT_ELEMENT_COLOR;
}

/** True when element `a` has the advantage over `b` (+1 attack and defense). */
export function elementBeats(a, b) {
  return a !== b && !!findElement(a)?.beats.includes(b);
}

/** All (winner, loser) pairs, in roster order — what ElementsGuideModal lists. */
export function elementAdvantagePairs() {
  return ARENA_ELEMENTS.flatMap(el => el.beats.filter(loser => !!findElement(loser)).map(loser => ({
    winner: el.key,
    loser
  })));
}

/**
 * Lowercase slug, letters/digits/underscore only — mirrors
 * ElementRegistry::sanitizeKey(), including the 20-character cap that keeps
 * a key inside the arena_cards.element column.
 */
export const ELEMENT_KEY_MAX_LENGTH = 20;
export function sanitizeElementKey(key) {
  return key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, ELEMENT_KEY_MAX_LENGTH);
}

/**
 * Derives a key from a display name, appending a numeric suffix if that key
 * is already taken. Latin-only by design (see sanitizeElementKey): a
 * Cyrillic name sanitizes to '', so fall back to a generic slug.
 */
export function elementKeyFromName(name, taken) {
  const base = sanitizeElementKey(name) || 'element';
  if (!taken.includes(base)) return base;
  let i = 2;
  while (taken.includes(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

/**
 * Resolves the roster the way ElementRegistry does server-side, layered so
 * nothing breaks on an install that predates the forumaker-arena.elements
 * setting:
 *
 *   1. the five builtins, with the original cycle as their `beats`;
 *   2. the legacy element_colors / element_icons override maps;
 *   3. entries from the `elements` setting — overriding a builtin when the
 *      key matches, appended as a brand-new element otherwise.
 *
 * Used by the admin panel, which edits the raw setting; the forum gets the
 * already-resolved list from the `arenaElements` forum attribute instead.
 */
export function resolveElements(raw, legacyColors, legacyIcons) {
  if (legacyColors === void 0) {
    legacyColors = {};
  }
  if (legacyIcons === void 0) {
    legacyIcons = {};
  }
  const resolved = BUILTIN_ELEMENTS.map(e => ({
    key: e.key,
    name: null,
    icon: legacyIcons[e.key] || e.icon,
    color: legacyColors[e.key] || e.color,
    beats: [...e.beats],
    builtin: true
  }));
  for (const entry of parseElementList(raw)) {
    const existing = resolved.findIndex(e => e.key === entry.key);
    if (existing === -1) resolved.push(entry);else resolved[existing] = entry;
  }

  // Advantages can dangle — a builtin's default `beats` points at another
  // builtin, and the stored payload may have removed the element on the
  // receiving end.
  const keys = resolved.map(e => e.key);
  for (const el of resolved) {
    el.beats = el.beats.filter(b => b !== el.key && keys.includes(b));
  }
  return resolved;
}

/** Parses the raw setting/attribute value (JSON string or decoded array), dropping malformed entries. */
export function parseElementList(raw) {
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw || '[]');
    } catch {
      parsed = [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  const seen = new Set();
  return parsed.map(e => {
    const key = sanitizeElementKey(String(e?.key ?? ''));
    if (!key || seen.has(key)) return null;
    seen.add(key);
    const builtin = BUILTIN_ELEMENTS.find(b => b.key === key);
    const name = String(e?.name ?? '').trim();
    return {
      key,
      name: name || null,
      icon: String(e?.icon ?? '').trim() || builtin?.icon || DEFAULT_ELEMENT_ICON,
      color: String(e?.color ?? '').trim() || builtin?.color || DEFAULT_ELEMENT_COLOR,
      beats: Array.isArray(e?.beats) ? Array.from(new Set(e.beats.map(b => sanitizeElementKey(String(b))))).filter(b => !!b && b !== key) : [],
      builtin: !!builtin
    };
  }).filter(e => e !== null);
}

/**
 * Live, read-only views of the roster shaped like the plain
 * Record<string, string> maps that ELEMENT_LABELS / ELEMENT_ICONS /
 * ELEMENT_COLORS used to be.
 *
 * They exist so the ~20 call sites that read `ELEMENT_COLORS[card.element]`
 * keep working unchanged now that the roster is dynamic — a plain object
 * would have to be re-synced by hand every time the roster changes, and any
 * missed spot would silently serve stale colors. Reads go straight to
 * ARENA_ELEMENTS, and Object.keys/values/entries enumerate the current
 * roster (hence the ownKeys/getOwnPropertyDescriptor traps).
 *
 * Writes are ignored: the roster is edited through the admin panel and
 * loaded via setArenaElements(), never by assigning into these maps.
 *
 * A key that isn't in the roster reads as undefined rather than as a default
 * — call sites already write `ELEMENT_COLORS[el] || '#888'`, and a map that
 * answered every possible property with a string would break anything that
 * probes for `toJSON`, `then` and friends.
 */
function elementMap(read) {
  return new Proxy({}, {
    get: (_target, prop) => typeof prop === 'string' && findElement(prop) ? read(prop) : undefined,
    has: (_target, prop) => typeof prop === 'string' && !!findElement(prop),
    set: () => true,
    ownKeys: () => elementKeys(),
    getOwnPropertyDescriptor: (_target, prop) => typeof prop === 'string' && findElement(prop) ? {
      value: read(prop),
      enumerable: true,
      configurable: true,
      writable: false
    } : undefined
  });
}
export const ELEMENT_LABELS = elementMap(elementLabel);
export const ELEMENT_ICONS = elementMap(elementIcon);
export const ELEMENT_COLORS = elementMap(elementColor);
flarum.reg.add('forumaker-arena', 'common/elements', { DEFAULT_ELEMENT_COLOR: DEFAULT_ELEMENT_COLOR,DEFAULT_ELEMENT_ICON: DEFAULT_ELEMENT_ICON,BUILTIN_ELEMENTS: BUILTIN_ELEMENTS,isBuiltinElement: isBuiltinElement,ARENA_ELEMENTS: ARENA_ELEMENTS,setArenaElements: setArenaElements,findElement: findElement,elementKeys: elementKeys,elementLabel: elementLabel,elementIcon: elementIcon,elementColor: elementColor,elementBeats: elementBeats,elementAdvantagePairs: elementAdvantagePairs,ELEMENT_KEY_MAX_LENGTH: ELEMENT_KEY_MAX_LENGTH,sanitizeElementKey: sanitizeElementKey,elementKeyFromName: elementKeyFromName,resolveElements: resolveElements,parseElementList: parseElementList,ELEMENT_LABELS: ELEMENT_LABELS,ELEMENT_ICONS: ELEMENT_ICONS,ELEMENT_COLORS: ELEMENT_COLORS, });